import React, { useContext, useState } from "react";

import { UserContext, useEntitlements } from "../contexts";
import { buildListingBroadcastMessage } from "../listings/helpers";
import { SendToWhatsAppSheet } from "./SendToWhatsAppSheet";
import { canUseWaBroadcast, isActiveStoreItem, listingImageIds, requestImageIds } from "./waBroadcastHelpers";

const watchName = (product, fallback) =>
  [product?.brand?.name, product?.model?.name, product?.reference_number].filter(Boolean).join(" · ") || fallback;

export const listingSubject = (listing) => ({
  subjectType: "listing",
  subjectId: listing.id,
  label: watchName(listing.product, `Listing #${listing.id}`),
  caption: buildListingBroadcastMessage(listing),
  imageIds: listingImageIds(listing),
});

export const requestSubject = (request) => ({
  subjectType: "request",
  subjectId: request.id,
  label: watchName(request.product, `Request #${request.id}`),
  caption: request.broadcastWhatsappMessage || "",
  imageIds: requestImageIds(request),
  // Order (buy) requests go out with no closing line (Alex, 2026-10-13).
  noClosingLine: request.request_type === "order",
});

/**
 * Wire "Share to Broadcast" (the WhatsApp Broadcast sheet) into any surface:
 *
 *   const wa = useSendToWhatsApp();
 *   wa.canSend(listing) && <MenuItem onSelect={() => wa.open(listingSubject(listing))} />
 *   {wa.sheet}
 *
 * Owners and store admins of the active store (and master admins), for that
 * store's own items only, when the store's plan has the "WhatsApp Broadcast"
 * row — the backend enforces all three.
 */
export function useSendToWhatsApp() {
  const { user, accessibleStores, activeStoreId } = useContext(UserContext);
  const [subject, setSubject] = useState(null);
  const { hasFeature } = useEntitlements();
  const allowed = canUseWaBroadcast(user, accessibleStores, activeStoreId, hasFeature);

  return {
    canSend: (item) => allowed && !!item && isActiveStoreItem(item, activeStoreId),
    open: setSubject,
    sheet: subject ? <SendToWhatsAppSheet open onClose={() => setSubject(null)} {...subject} /> : null,
  };
}
